import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { App, Button, Card, Checkbox, Collapse, Empty, Form, Input, List, Select, Space, Tag, Typography } from 'antd';
import dayjs from 'dayjs';
import { useAuth } from '../../auth/AuthContext';
import { ROLES } from '../../constants/roles';
import { reviewsApi } from '../../api/reviews.api';
import { toApiError } from '../../api/client';
import AiAnalysisBadge from '../../components/AiAnalysisBadge';
import type { CallDetail } from '../../types/calls';
import type { RiskLevel } from '../../types/domain';
import { FINAL_STATUS_LABELS, type FinalStatus } from '../../types/reviews';

interface ReviewForm {
  finalStatus: FinalStatus;
  notes?: string;
  flaggedForFollowUp?: boolean;
  correctedSummary?: string;
  correctedTopic?: string;
  confirmOutputs?: boolean;
}

const RISK_OPTIONS = [
  { value: 'Low', label: 'کم' },
  { value: 'Medium', label: 'متوسط' },
  { value: 'High', label: 'زیاد' },
];

export default function ReviewPanel({ callId, call }: { callId: number; call: CallDetail }) {
  const { hasRole } = useAuth();
  const { message } = App.useApp();
  const qc = useQueryClient();
  const [form] = Form.useForm<ReviewForm>();

  const canReview = hasRole([ROLES.Supervisor, ROLES.QualityControlSpecialist]);
  const reviews = useQuery({ queryKey: ['reviews', callId], queryFn: () => reviewsApi.list(callId) });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['reviews', callId] });
    void qc.invalidateQueries({ queryKey: ['call', callId] });
  };
  const onError = (err: unknown) => message.error(toApiError(err).message);

  const submit = useMutation({
    mutationFn: (v: ReviewForm) => {
      // Per-field AI override: only send fields the reviewer actually changed.
      const corrected: Record<string, string> = {};
      if (v.correctedSummary != null && v.correctedSummary !== (call.analysis?.summary ?? '')) corrected.summary = v.correctedSummary;
      if (v.correctedTopic != null && v.correctedTopic !== (call.analysis?.topic ?? '')) corrected.topic = v.correctedTopic;
      return reviewsApi.submit(callId, {
        finalStatus: v.finalStatus,
        notes: v.notes,
        flaggedForFollowUp: !!v.flaggedForFollowUp,
        correctedOutputs: Object.keys(corrected).length ? JSON.stringify(corrected) : undefined,
        confirmedOutputs: v.confirmOutputs && call.analysis
          ? JSON.stringify({ summary: call.analysis.summary, topic: call.analysis.topic, riskLevel: call.riskLevel })
          : undefined,
      });
    },
    onSuccess: () => { message.success('بازبینی ثبت شد'); form.resetFields(); invalidate(); },
    onError,
  });

  const overrideRisk = useMutation({
    mutationFn: (riskLevel: RiskLevel) => reviewsApi.overrideRisk(callId, { riskLevel }),
    onSuccess: () => { message.success('سطح ریسک به‌روزرسانی شد'); invalidate(); },
    onError,
  });

  const training = useMutation({
    mutationFn: (value: boolean) => reviewsApi.trainingCase(callId, value),
    onSuccess: () => { message.success('به‌روزرسانی شد'); invalidate(); },
    onError,
  });

  return (
    <Space direction="vertical" size="large" style={{ display: 'flex' }}>
      {canReview && (
        <Card size="small" title="ثبت بازبینی">
          <Form<ReviewForm>
            form={form}
            layout="vertical"
            onFinish={(v) => submit.mutate(v)}
            initialValues={{ correctedSummary: call.analysis?.summary ?? '', correctedTopic: call.analysis?.topic ?? '' }}
          >
            <Form.Item name="finalStatus" label="وضعیت نهایی" rules={[{ required: true, message: 'وضعیت نهایی الزامی است' }]}>
              <Select
                options={Object.entries(FINAL_STATUS_LABELS).map(([value, label]) => ({ value, label }))}
                placeholder="انتخاب کنید"
              />
            </Form.Item>
            <Form.Item name="notes" label="یادداشت">
              <Input.TextArea rows={3} />
            </Form.Item>
            <Form.Item name="flaggedForFollowUp" valuePropName="checked">
              <Checkbox>نیازمند پیگیری</Checkbox>
            </Form.Item>

            {call.analysis && (
              <Collapse
                ghost
                style={{ marginBottom: 12 }}
                items={[{
                  key: 'ai-override',
                  label: 'اصلاح خروجی‌های هوش مصنوعی (اختیاری)',
                  children: (
                    <>
                      <Form.Item name="correctedSummary" label="خلاصهٔ اصلاح‌شده">
                        <Input.TextArea rows={2} />
                      </Form.Item>
                      <Form.Item name="correctedTopic" label="موضوع اصلاح‌شده">
                        <Input />
                      </Form.Item>
                      <Form.Item name="confirmOutputs" valuePropName="checked" style={{ marginBottom: 0 }}>
                        <Checkbox>تأیید خروجی‌های AI بدون تغییر</Checkbox>
                      </Form.Item>
                    </>
                  ),
                }]}
              />
            )}

            <Button type="primary" htmlType="submit" loading={submit.isPending}>ثبت بازبینی</Button>
          </Form>
        </Card>
      )}

      {canReview && (
        <Card size="small" title="اقدامات">
          <Space wrap size="large">
            <Space>
              <Space size={4} data-testid="review-risk-label">
                <Typography.Text>لغو/تغییر ریسک:</Typography.Text>
                <AiAnalysisBadge compact />
              </Space>
              <Select
                value={call.riskLevel}
                style={{ width: 120 }}
                options={RISK_OPTIONS}
                loading={overrideRisk.isPending}
                disabled={overrideRisk.isPending}
                onChange={(v) => overrideRisk.mutate(v as RiskLevel)}
              />
            </Space>
            <Checkbox
              checked={call.trainingCaseFlag}
              disabled={training.isPending}
              onChange={(e) => training.mutate(e.target.checked)}
            >
              مورد آموزشی
            </Checkbox>
          </Space>
        </Card>
      )}

      <Card size="small" title="بازبینی‌های ثبت‌شده">
        {reviews.data && reviews.data.length > 0 ? (
          <List
            dataSource={reviews.data}
            renderItem={(r) => (
              <List.Item>
                <Space direction="vertical" size={2} style={{ width: '100%' }}>
                  <Space>
                    <Tag color={r.finalStatus === 'Approved' ? 'green' : r.finalStatus === 'Escalated' ? 'red' : 'orange'}>
                      {r.finalStatus ? (FINAL_STATUS_LABELS[r.finalStatus] ?? r.finalStatus) : '—'}
                    </Tag>
                    <Typography.Text strong>{r.reviewerName}</Typography.Text>
                    <Typography.Text type="secondary">{dayjs(r.createdAt).format('YYYY-MM-DD HH:mm')}</Typography.Text>
                    {r.flaggedForFollowUp && <Tag color="blue">پیگیری</Tag>}
                  </Space>
                  {r.notes && <Typography.Text>{r.notes}</Typography.Text>}
                </Space>
              </List.Item>
            )}
          />
        ) : (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="هنوز بازبینی‌ای ثبت نشده است." />
        )}
      </Card>
    </Space>
  );
}
